import type { ApproveDocsRow, ApproveDocsResponse } from './types'
import type { BookObjectReview, BookReview, BookReviewAction } from './bookReview'
import type { BookReviewScope } from './bookReviewQuery'

export type BookServerActionKind = 'book' | 'book_all' | 'plug' | 'blocked' | 'pending'

export interface BookServerAction {
  id: string
  label: string
  kind?: BookServerActionKind
  action?: BookServerActionKind
  proposal_id?: string | null
  object_id?: string | null
  selected_proposal_ids?: readonly string[]
  disabled?: boolean
  disabled_reason?: string | null
  also_books?: readonly string[]
  task_id?: string | null
}

export interface BookFieldEdits {
  [field: string]: unknown
}

export interface BookExecuteScope {
  client_id: string | null
  fund_id: string | null
}

export interface BookEditedField {
  original: unknown
  corrected: unknown
}

/** What the officer typed: the page and the passage that states the date. */
export interface BookDateCitationInput {
  page: string
  text: string
}

export interface BookDateCitation {
  page: number
  text: string
}

export interface BookExecuteRequest {
  date_citations?: Record<string, BookDateCitation>
  final_terms_proposal_ids?: readonly string[]
  nominal_review_proposal_ids?: readonly string[]
  deck_version: string
  proposal_ids: readonly string[]
  presented_proposal_ids: readonly string[]
  scope: BookExecuteScope
  review_binding_value: string
  edited_fields: Record<string, Record<string, BookEditedField>>
  /** Explicit "distinct event" answer: proposal -> the exact booked twins shown. */
  twin_distinct?: Record<string, BookTwinAnswer>
}

/** What the officer was shown: the booked twins and their challenge revision. */
export interface BookTwinAnswer {
  twin_trade_ids: readonly string[]
  twin_revision: string
}

/** One selected movement the server found repeating a booked trade. */
export interface BookTwinConflict {
  proposal_id: string
  distinct_available: boolean
  twin_trade_ids?: readonly string[]
  twin_revision?: string
}

/** The Book execute 409 of the confirmed-twin check, or null for any other error. */
export function bookTwinConflicts(error: unknown): BookTwinConflict[] | null {
  if (typeof error !== 'object' || !error || !('status' in error) || error.status !== 409)
    return null
  const body = 'body' in error ? (error as { body?: unknown }).body : undefined
  const detail = (body as { detail?: unknown } | undefined)?.detail
  if (!detail || typeof detail !== 'object') return null
  const { code, items } = detail as { code?: unknown; items?: unknown }
  if (code !== 'confirmed_twin' || !Array.isArray(items) || !items.length) return null
  const conflicts: BookTwinConflict[] = []
  for (const item of items) {
    if (!item || typeof item !== 'object') return null
    const { proposal_id, distinct_available, twin_trade_ids, twin_revision } = item as Record<
      string,
      unknown
    >
    if (typeof proposal_id !== 'string' || typeof distinct_available !== 'boolean') return null
    if (
      distinct_available &&
      (!Array.isArray(twin_trade_ids) ||
        !twin_trade_ids.length ||
        !twin_trade_ids.every((id) => typeof id === 'string') ||
        typeof twin_revision !== 'string' ||
        !/^[0-9a-f]{64}$/.test(twin_revision))
    )
      return null
    conflicts.push(
      distinct_available
        ? {
            proposal_id,
            distinct_available,
            twin_trade_ids: twin_trade_ids as string[],
            twin_revision: twin_revision as string,
          }
        : { proposal_id, distinct_available }
    )
  }
  return conflicts
}

/** The explicit answer for exactly these conflicts; null when any cannot be answered. */
export function twinDistinctAnswer(
  conflicts: readonly BookTwinConflict[]
): Record<string, BookTwinAnswer> | null {
  if (
    !conflicts.length ||
    conflicts.some((item) => !item.distinct_available || !item.twin_revision)
  )
    return null
  // Carried verbatim: the server compares the shown revision before anything else.
  return Object.fromEntries(
    conflicts.map((item) => [
      item.proposal_id,
      {
        twin_trade_ids: [...(item.twin_trade_ids ?? [])].sort(),
        twin_revision: item.twin_revision as string,
      },
    ])
  )
}

export interface BookReceiptItemLike {
  proposal_id: string
  state: string
  record_event_ids?: readonly string[]
  target_type?: string | null
  target_id?: string | null
  head_revisions?: Record<string, string>
  reason?: string | null
}

export interface BookReceiptLike {
  state: string
  receipts?: readonly BookReceiptItemLike[]
}

export type BookMutationStatus =
  'idle' | 'submitting' | 'pending' | 'applied' | 'stale' | 'refused' | 'error'

export interface BookMutationState {
  status: BookMutationStatus
  outcomes: ReadonlyArray<ApproveDocsResponse['outcomes'][number]>
  message: string | null
}

export function actionKind(action: BookServerAction): BookServerActionKind {
  return action.kind ?? action.action ?? 'book'
}

export function isBookAction(action: BookServerAction): boolean {
  return actionKind(action) === 'book' && !!action.proposal_id && !action.disabled
}

export function selectableActions(
  actions: readonly BookServerAction[]
): readonly BookServerAction[] {
  return actions.filter(isBookAction)
}

function selectedProposalIds(action: BookServerAction): readonly string[] {
  return action.selected_proposal_ids?.length
    ? action.selected_proposal_ids.map(String)
    : action.proposal_id
      ? [action.proposal_id, ...(action.also_books ?? []).map(String)]
      : []
}

export function displayActions(
  actions: readonly BookServerAction[],
  pendingActionId?: string | null
): readonly BookReviewAction[] {
  const displayed = actions
    .filter((action) => ['book', 'book_all', 'blocked', 'pending'].includes(actionKind(action)))
    .map<BookReviewAction>((action) => ({
      id: action.id,
      label: action.label,
      proposal_id: action.proposal_id,
      object_id: action.object_id,
      kind: actionKind(action) === 'book_all' ? 'book_all' : 'book',
      disabled: action.disabled === true || ['blocked', 'pending'].includes(actionKind(action)),
      disabled_reason: action.disabled_reason ?? null,
      pending: actionKind(action) === 'pending' || pendingActionId === action.id,
    }))
  return displayed
}

export function displayReview(
  review: BookReview,
  actions: readonly BookServerAction[],
  pendingActionId?: string | null
): BookReview {
  return { ...review, actions: displayActions(actions, pendingActionId) }
}

export function buildApproveRows(
  selectedActionIds: readonly string[],
  actions: readonly BookServerAction[],
  edits: Readonly<Record<string, BookFieldEdits>> = {}
): ApproveDocsRow[] {
  const selected = new Set(selectedActionIds)
  const plans = actions.filter(
    (action) => selected.has(action.id) && isBookAction(action) && action.proposal_id
  )
  const rows: ApproveDocsRow[] = []
  const included = new Set<string>()
  const add = (proposalId: string, edit?: BookFieldEdits) => {
    if (included.has(proposalId)) return
    included.add(proposalId)
    rows.push({
      proposal_id: proposalId,
      action: 'approve',
      ...(edit && Object.keys(edit).length ? { edited_fields: edit } : {}),
    })
  }
  for (const plan of plans) {
    for (const proposalId of selectedProposalIds(plan)) add(proposalId, edits[proposalId])
  }
  return rows
}

function valueOrNull(value: unknown): unknown {
  return value === undefined ? null : value
}

function editedFieldsFor(
  review: BookReview,
  proposalIds: readonly string[],
  edits: Readonly<Record<string, BookFieldEdits>>
): Record<string, Record<string, BookEditedField>> {
  const selected = new Set(proposalIds)
  const result: Record<string, Record<string, BookEditedField>> = {}
  for (const [proposalId, fields] of Object.entries(edits)) {
    if (!selected.has(proposalId)) continue
    const object = review.objects.find(
      (candidate) => candidate.proposal_id === proposalId && candidate.role !== 'effect'
    )
    for (const [fieldKey, corrected] of Object.entries(fields)) {
      const field = object?.fields.find(
        (candidate) => (candidate.proposal_key ?? candidate.definition.key) === fieldKey
      )
      const original = field?.proposed.value ?? null
      result[proposalId] ??= {}
      result[proposalId][fieldKey] = {
        original: valueOrNull(original),
        corrected: valueOrNull(corrected),
      }
    }
  }
  return result
}

/** A trade whose date the officer is editing can carry a source citation. */
export function editsTradeDate(
  object: Pick<BookObjectReview, 'target_type' | 'proposal_id'>,
  edits: Readonly<Record<string, BookFieldEdits>>
): boolean {
  return (
    object.target_type === 'trade' &&
    Object.prototype.hasOwnProperty.call(edits[object.proposal_id] ?? {}, 'effective_date')
  )
}

/**
 * Only a complete citation of a selected trade whose date is actually edited
 * is sent. The server finds the passage on that page of the source itself;
 * nothing typed here is trusted as a date or as proof.
 */
export function dateCitationsFor(
  review: BookReview,
  proposalIds: readonly string[],
  edits: Readonly<Record<string, BookFieldEdits>>,
  citations: Readonly<Record<string, BookDateCitationInput>>
): Record<string, BookDateCitation> {
  const selected = new Set(proposalIds)
  const result: Record<string, BookDateCitation> = {}
  for (const [proposalId, citation] of Object.entries(citations)) {
    const object = review.objects.find(
      (candidate) => candidate.proposal_id === proposalId && candidate.role !== 'effect'
    )
    const page = /^[1-9][0-9]*$/.test(citation.page.trim()) ? Number(citation.page.trim()) : null
    const text = citation.text.trim()
    if (!selected.has(proposalId) || !object || !editsTradeDate(object, edits)) continue
    if (page === null || !Number.isSafeInteger(page) || !text || text.length > 500) continue
    result[proposalId] = { page, text }
  }
  return result
}

export type BookPreviewRequest = Omit<BookExecuteRequest, 'proposal_ids' | 'date_citations'>

export function buildBookPreviewRequest(
  review: BookReview,
  deckVersion: string,
  scope: BookReviewScope = {},
  edits: Readonly<Record<string, BookFieldEdits>> = {},
  nominalReviewIds: readonly string[] = []
): BookPreviewRequest {
  const proposalIds = review.objects
    .filter((object) => object.role !== 'effect')
    .map((object) => object.proposal_id)
    .filter((id, index, ids) => id && ids.indexOf(id) === index)
  return {
    deck_version: deckVersion,
    presented_proposal_ids: proposalIds,
    scope: { client_id: scope.clientId ?? null, fund_id: scope.fundId ?? null },
    review_binding_value: review.binding.value,
    edited_fields: editedFieldsFor(review, proposalIds, edits),
    ...(nominalReviewIds.length
      ? { nominal_review_proposal_ids: [...nominalReviewIds].sort() }
      : {}),
  }
}

export function buildBookExecuteRequest(
  selectedActionIds: readonly string[],
  actions: readonly BookServerAction[],
  review: BookReview,
  deckVersion: string,
  scope: BookReviewScope = {},
  edits: Readonly<Record<string, BookFieldEdits>> = {},
  finalTermsIds: readonly string[] = [],
  nominalReviewIds: readonly string[] = [],
  dateCitations: Readonly<Record<string, BookDateCitationInput>> = {}
): BookExecuteRequest {
  const selected = new Set(selectedActionIds)
  const proposalIds: string[] = []
  const included = new Set<string>()
  const add = (proposalId: string) => {
    if (!included.has(proposalId)) {
      included.add(proposalId)
      proposalIds.push(proposalId)
    }
  }
  for (const action of actions) {
    if (!selected.has(action.id) || action.disabled) continue
    if (actionKind(action) === 'book_all') {
      for (const proposalId of selectedProposalIds(action)) add(proposalId)
      continue
    }
    if (!isBookAction(action)) continue
    for (const proposalId of selectedProposalIds(action)) add(proposalId)
  }
  // Bind only objects actually presented for review. Group snapshot rows
  // also include unseen siblings; they are not additional officer decisions.
  const presentedProposalIds = review.objects
    .filter((object) => object.role !== 'effect')
    .map((object) => object.proposal_id)
    .filter((proposalId, index, ids) => proposalId && ids.indexOf(proposalId) === index)
  const finalTerms = [...new Set(finalTermsIds)]
    .filter(
      (id) =>
        included.has(id) &&
        review.objects.some(
          (o) => o.proposal_id === id && o.role !== 'effect' && o.final_terms_review
        )
    )
    .sort()
  const nominalIds = [...new Set(nominalReviewIds)]
    .filter(
      (id) =>
        included.has(id) &&
        review.objects.some(
          (o) => o.proposal_id === id && o.role !== 'effect' && o.nominal_citation_review
        )
    )
    .sort()
  const citations = dateCitationsFor(review, proposalIds, edits, dateCitations)
  return {
    ...(Object.keys(citations).length ? { date_citations: citations } : {}),
    ...(nominalIds.length ? { nominal_review_proposal_ids: nominalIds } : {}),
    ...(finalTerms.length ? { final_terms_proposal_ids: finalTerms } : {}),
    deck_version: deckVersion,
    proposal_ids: proposalIds,
    presented_proposal_ids: presentedProposalIds,
    scope: {
      client_id: scope.clientId ?? null,
      fund_id: scope.fundId ?? null,
    },
    review_binding_value: review.binding.value,
    edited_fields: editedFieldsFor(review, proposalIds, edits),
  }
}

function requestHash(value: string): string {
  let hash = 2166136261
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index)
    hash = Math.imul(hash, 16777619)
  }
  return (hash >>> 0).toString(16).padStart(8, '0')
}

export function stableBookIdempotencyKey(
  groupId: string,
  actionId: string,
  request: BookExecuteRequest
): string {
  const digest = requestHash(JSON.stringify(request))
  return ['nolos-book', groupId, actionId, digest]
    .map((part) => part.replace(/[^A-Za-z0-9._~-]/g, '_'))
    .join('-')
}

export function mutationFromBookReceipt(receipt: BookReceiptLike): BookMutationState {
  const outcomes = (receipt.receipts ?? []).map((item) => ({
    proposal_id: item.proposal_id,
    status: item.state,
  }))
  if (receipt.state === 'applied') {
    return {
      status: 'applied',
      outcomes,
      message: 'Applied to the Record. The review remains visible until it refreshes.',
    }
  }
  if (
    receipt.state === 'refused' ||
    receipt.state === 'conflicted' ||
    receipt.state === 'deferred'
  ) {
    const reason = receipt.receipts?.find((item) => item.reason)?.reason
    return {
      status: 'refused',
      outcomes,
      message:
        reason ??
        (receipt.state === 'deferred'
          ? 'Book is deferred; refresh the review before deciding again.'
          : 'The Book request was refused; review the current Record before retrying.'),
    }
  }
  return {
    status: 'pending',
    outcomes,
    message:
      receipt.state === 'deferred'
        ? 'Book is deferred; the review remains open.'
        : 'Submitted — awaiting the application receipt.',
  }
}

export function classifyBookMutationError(error: unknown): 'stale' | 'refused' | 'error' {
  const status = typeof error === 'object' && error && 'status' in error ? error.status : null
  const message = error instanceof Error ? error.message : String(error ?? '')
  if (status === 409 || /deck_changed|stale|changed during/i.test(message)) return 'stale'
  if (status === 422 || /refused|rejected|unsupported/i.test(message)) return 'refused'
  return 'error'
}

export function pendingMutation(outcomes: ApproveDocsResponse['outcomes']): BookMutationState {
  return { status: 'pending', outcomes, message: 'Submitted — awaiting the application receipt.' }
}

/** Officer wording of the explicit final-terms choice. draft_final keeps its
 *  exact wording; an explicit correction of a recorded trade is never called a
 *  draft or provisional trade; a signed commitment says plainly that it
 *  replaces the provisional amount and date and adds nothing. */
export function finalTermsChoiceText(
  object: Pick<BookObjectReview, 'identity' | 'title' | 'final_terms_review'>
): string {
  const subject =
    [object.identity.holder, object.identity.instrument].filter(Boolean).join(' · ') ||
    object.title ||
    'Trade'
  if (object.final_terms_review?.kind === 'explicit_correction') {
    return (
      `${subject}: correction of a recorded amount. This replacement source corrects ` +
      'the amount of this same recorded trade; nothing else changes. Apply it when I ' +
      'book. This does not confirm payment.'
    )
  }
  if (object.final_terms_review?.kind === 'capital_final') {
    return (
      `${subject}: this signed agreement finalises this same provisional commitment. ` +
      'Its signed amount and date replace the provisional ones on the same trade; ' +
      'nothing is added. Apply it when I book. This does not confirm payment.'
    )
  }
  return (
    `${subject}: these final terms describe this same provisional trade. Apply them ` +
    'when I book. This does not confirm payment.'
  )
}
